import { Box, Divider, Flex, HStack, Icon, Text, VStack } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { PiLightningDuotone as FiZap, PiEyeDuotone as FiEye, PiLockDuotone as FiLock, PiSealCheckDuotone as FiUserCheck } from "react-icons/pi";
import { CreateVotingViewModel } from "../CreateVotingViewModel";

const ELECTORATE: Record<string, string> = {
  closed: "Lista imienna",
  signup: "Zapisy (vox populi)",
  open: "Referendum otwarte"
};
const CONFIRM: Record<string, string> = {
  in_session: "W sesji",
  email: "Mail / aplikacja",
  app: "Tylko aplikacja"
};
const WALLET: Record<string, string> = {
  none: "Brak",
  recommended: "Zalecany",
  required: "Wymagany"
};

const Row: React.FC<{ label: string; value: string; accent?: boolean }> = ({ label, value, accent }) => (
  <Flex justify="space-between" align="baseline" gap="10px">
    <Text fontSize="12px" color="gray.500" flexShrink={0}>
      {label}
    </Text>
    <HStack spacing="5px" justify="flex-end" minW={0}>
      {accent && <Icon as={FiZap} boxSize="12px" color="accent" />}
      <Text fontSize="13px" fontWeight="700" color={accent ? "accent" : "primary"} textAlign="right" noOfLines={1}>
        {value}
      </Text>
    </HStack>
  </Flex>
);

const Chip: React.FC<{ icon: React.ElementType; label: string }> = ({ icon, label }) => (
  <HStack spacing="4px" bg="rgba(67,0,152,0.06)" borderRadius="full" px="8px" py="2px">
    <Icon as={icon} boxSize="10px" color="primary" />
    <Text fontSize="10.5px" fontWeight="700" color="primary">
      {label}
    </Text>
  </HStack>
);

/** Mini-podgląd karty głosowania — „tak zobaczy to wyborca". */
const BallotPreview: React.FC<{ model: CreateVotingViewModel }> = observer(({ model }) => {
  const title = model.votingDataModel.name?.trim();
  const questions = Array.from(model.votingQuestionsModel.questions.values());
  const first = questions[0];
  const options = first ? Array.from(first.answers.values()).map(a => a.content?.trim()).filter(Boolean) : [];

  return (
    <Box border="1px solid rgba(67,0,152,0.12)" borderRadius="14px" bg="#FCFBFE" p="14px" boxShadow="0 4px 14px rgba(67,0,152,0.05)">
      <HStack spacing="6px" mb="9px">
        <Chip icon={model.isSecret ? FiLock : FiUserCheck} label={model.isSecret ? "Tajne" : "Jawne"} />
        <Chip icon={FiEye} label={model.isPublic ? "Publiczne" : "Prywatne"} />
      </HStack>
      <Text fontSize="14px" fontWeight="800" color={title ? "primary" : "gray.400"} mb="10px" noOfLines={2}>
        {title || "Tytuł głosowania"}
      </Text>
      {first?.question?.trim() ? (
        <Box>
          <Text fontSize="12.5px" fontWeight="700" color="gray.700" mb="7px" noOfLines={2}>
            {first.question}
          </Text>
          <VStack align="stretch" spacing="6px">
            {options.slice(0, 3).map((opt, i) => (
              <HStack key={i} spacing="8px">
                <Box w="13px" h="13px" borderRadius="full" border="2px solid" borderColor="rgba(67,0,152,0.3)" flexShrink={0} />
                <Text fontSize="12.5px" color="gray.600" noOfLines={1}>
                  {opt}
                </Text>
              </HStack>
            ))}
            {options.length > 3 && (
              <Text fontSize="11.5px" color="gray.400" pl="21px">
                +{options.length - 3} więcej
              </Text>
            )}
          </VStack>
        </Box>
      ) : (
        <Text fontSize="12px" color="gray.400">
          Pytania dodasz w kroku „Pytania" — pojawią się tutaj.
        </Text>
      )}
    </Box>
  );
});

/** Żywe podsumowanie wyborów organizatora — składa decyzje w czytelny obraz „Twoje głosowanie". */
export const LiveSummary: React.FC<{ model: CreateVotingViewModel }> = observer(({ model }) => {
  const identity = model.requiresVerification
    ? "Zweryfikowana tożsamość"
    : model.accountRequired
    ? "Zalogowane konto"
    : "Bez konta";

  const visibility = model.isPublic === true ? "Publiczne" : model.isPublic === false ? "Niepubliczne" : "—";

  return (
    <Box>
      <Text fontSize="12px" fontWeight="800" letterSpacing="0.08em" color="#5a5468" textTransform="uppercase" mb="12px">
        Twoje głosowanie
      </Text>
      <VStack align="stretch" spacing="10px">
        <Row label="Tajność" value={model.isSecret ? "Tajne" : "Jawne imienne"} />
        <Row label="Widoczność" value={visibility} />
        <Row label="Tożsamość" value={identity} />
        <Row label="Elektorat" value={ELECTORATE[model.electorateType] ?? "—"} />
        <Row label="Potwierdzenie" value={CONFIRM[model.voteConfirmation] ?? "—"} />
        {!model.isSecret && (
          <Row label="Podpis tożsamością" value={WALLET[model.walletPolicy] ?? "Brak"} accent={model.walletPolicy === "required"} />
        )}
      </VStack>

      <Divider my="16px" borderColor="rgba(24,12,48,0.08)" />

      <Text fontSize="12px" fontWeight="800" letterSpacing="0.08em" color="#5a5468" textTransform="uppercase" mb="10px">
        Tak zobaczy to wyborca
      </Text>
      <BallotPreview model={model} />
    </Box>
  );
});
